import {
  ArrowRight,
  Award,
  Bell,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  CircleDollarSign,
  Copy,
  Download,
  Gift,
  HandCoins,
  Headphones,
  IdCard,
  Info,
  LogOut,
  MessageSquare,
  Repeat2,
  Shield,
  TicketCheck,
  TrendingUp,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { formatUsd } from "../utils/money";
import { useSession } from "../store/session";
import { useCopy } from "../i18n/copy";
import { useQuery } from "@tanstack/react-query";
import { engagementApi } from "../api/engagement";
import { HomeNoticeTicker } from "../components/HomeCommunications";

const isAppDomain = window.location.hostname.toLowerCase().startsWith("app.");

const menuGroups = [
  {
    title: "Dinero y registros",
    items: [
      {
        label: "Métodos de retiro",
        hint: "Cuenta bancaria y datos de pago",
        to: "/wallet/payout-methods",
        icon: IdCard,
      },
      {
        label: "Historial de movimientos",
        hint: "Depósitos, retiros e ingresos",
        to: "/wallet/transactions",
        icon: TicketCheck,
      },
      {
        label: "Historial de retiros",
        hint: "",
        to: "/wallet/withdrawals",
        icon: HandCoins,
      },
      {
        label: "Transferir saldo del equipo",
        hint: "",
        to: "/wallet/team-transfer",
        icon: Repeat2,
      },
    ],
  },
  {
    title: "Comunidad y beneficios",
    items: [
      {
        label: "Mi equipo",
        hint: "Actividad de niveles y miembros",
        to: "/growth/team",
        icon: UsersRound,
      },
      {
        label: "Ingresos del equipo",
        hint: "",
        to: "/growth/income",
        icon: TrendingUp,
      },
      {
        label: "Invitar amigos",
        hint: "Código, enlace y recompensas",
        to: "/growth/invite",
        icon: Gift,
      },
      {
        label: "Puntos y premios",
        hint: "Canjes y beneficios disponibles",
        to: "/rewards",
        icon: Award,
      },
    ],
  },
  {
    title: "Ayuda y configuración",
    items: [
      {
        label: "Mensajes",
        hint: "Novedades de tu cuenta",
        to: "/messages",
        icon: MessageSquare,
      },
      {
        label: "Seguridad y perfil",
        hint: "Datos personales y contraseña",
        to: "/account/profile",
        icon: Shield,
      },
      {
        label: "Servicio al cliente",
        hint: "Comunícate con nuestro equipo",
        to: "/support",
        icon: Headphones,
      },
      {
        label: "Descargar aplicación",
        hint: "Acceso a la versión oficial",
        to: "/download",
        icon: Download,
      },
      {
        label: "Reclutamiento",
        hint: "",
        to: "/recruitment",
        icon: BriefcaseBusiness,
      },
      {
        label: "Sobre GOP ALA",
        hint: "",
        to: "/about",
        icon: Info,
      },
      {
        label: "Perfil de la empresa",
        hint: "",
        to: "/company",
        icon: Building2,
      },
    ],
  },
];

export function AccountPage() {
  const { user, isPreview, logout } = useSession();
  const navigate = useNavigate();
  const { tr } = useCopy();
  const notice = useQuery({
    queryKey: ["notice", 5],
    queryFn: () => engagementApi.notice(5),
    enabled: !isPreview,
  });
  return (
    <div className="page account-page">
      <section className="account-hero">
        <div className="account-top">
          <div className="account-title">
            <p className="eyebrow light">{tr("Centro de cuenta")}</p>
            <h1>{tr("Mi espacio")}</h1>
          </div>
          <div className="account-top-actions">
            <Link
              className="hero-icon"
              to="/account/checkin"
              aria-label={tr("Revisar asistencia")}
            >
              <CheckCircle2 />
            </Link>
            <Link
              className="hero-icon page-message-link"
              to="/messages"
              aria-label={tr("Mensajes")}
            >
              <Bell />
            </Link>
          </div>
        </div>
        <div className="account-hero-body">
          <div className="profile-card">
            <span className="avatar">
              {user.avatar ? (
                <img src={user.avatar} alt={user.username} />
              ) : (
                user.username.slice(0, 1)
              )}
            </span>
            <div className="profile-name">
              <span className="level-chip">
                <Award /> {tr("Nivel")} {user.level}
              </span>
              <h2>{user.username}</h2>
              <button
                onClick={() =>
                  void navigator.clipboard.writeText(String(user.id))
                }
              >
                ID {user.id} <Copy />
              </button>
            </div>
            <Link className="button sun" to="/wallet/deposit">
              {tr("Depositar")} <ArrowRight />
            </Link>
          </div>
          <div className="level-progress">
            <div>
              <span>{tr("Progreso al siguiente nivel")}</span>
              <strong>
                {tr("Faltan")} {formatUsd(user.nextLevelBalance)}
              </strong>
            </div>
            <div className="progress-track">
              <i style={{ width: `${user.levelProgress}%` }} />
            </div>
          </div>
        </div>
      </section>
      <section className="account-assets">
        <span>
          <i className="account-asset-icon"><WalletCards /></i>
          <span>
            <small>{tr("Saldo")}</small>
            <strong>{formatUsd(user.balance)}</strong>
          </span>
        </span>
        <span>
          <i className="account-asset-icon"><Award /></i>
          <span>
            <small>{tr("Puntos")}</small>
            <strong>{user.score}</strong>
          </span>
        </span>
        <span>
          <i className="account-asset-icon"><TrendingUp /></i>
          <span>
            <small>{tr("Ingreso del equipo")}</small>
            <strong>{formatUsd(user.teamBalance)}</strong>
          </span>
        </span>
      </section>
      {notice.data?.content && (
        <div className="account-notice">
          <HomeNoticeTicker content={notice.data.content} />
        </div>
      )}
      <section className="account-actions">
        <Link to="/wallet/deposit">
          <span>
            <WalletCards />
          </span>
          <strong>{tr("Depositar")}</strong>
        </Link>
        <Link to="/wallet/withdraw">
          <span>
            <HandCoins />
          </span>
          <strong>{tr("Retirar")}</strong>
        </Link>
        <Link to="/wallet/transactions">
          <span>
            <CircleDollarSign />
          </span>
          <strong>{tr("Movimientos")}</strong>
        </Link>
        <Link to="/growth/team">
          <span>
            <UsersRound />
          </span>
          <strong>{tr("Mi equipo")}</strong>
        </Link>
      </section>
      <div className="account-menu-grid">
        {menuGroups.map((group) => (
          <section className="account-menu" key={group.title}>
            <h2>{tr(group.title)}</h2>
            <div>
              {group.items
                .filter(({ to }) => !(isAppDomain && to === "/download"))
                .map(({ label, to, icon: Icon }) => (
                  <Link to={to} key={to}>
                    <span className="menu-icon">
                      <Icon />
                    </span>
                    <span>
                      <strong>{tr(label)}</strong>
                    </span>
                    <ArrowRight />
                  </Link>
                ))}
            </div>
          </section>
        ))}
      </div>
      <button
        className="logout-button"
        onClick={() => {
          logout();
          navigate("/auth/login");
        }}
      >
        <LogOut /> {tr(isPreview ? "Ir al inicio de sesión" : "Cerrar sesión")}
      </button>
    </div>
  );
}
